import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';

import useRealtimeSubscription from '../../hooks/useRealtimeSubscription';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import VerificationProgressBar from './components/VerificationProgressBar';
import ScorePreviewBadge from './components/ScorePreviewBadge';
import InfluencerVerificationForm from './components/InfluencerVerificationForm';
import BrandVerificationForm from './components/BrandVerificationForm';
import VerificationTooltip from './components/VerificationTooltip';

const ProfileVerificationScreen = () => {
  const navigate = useNavigate();
  const { user, userRole, updateProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const [verificationStatus, setVerificationStatus] = useState(null); // real-time profile_verified

  // Determine role: from auth context or fallback to influencer
  const role = userRole || 'influencer';

  // Influencer form data
  const [influencerData, setInfluencerData] = useState({
    socialHandles: { instagram: '', tiktok: '', youtube: '', twitter: '' },
    followerRanges: { instagram: '', tiktok: '', youtube: '', twitter: '' },
    categories: [],
  });

  // Brand form data
  const [brandData, setBrandData] = useState({
    brandName: '',
    websiteUrl: '',
    businessCategory: '',
    campaignBudget: '',
  });

  // Real-time: track profile_verified changes
  useRealtimeSubscription({
    channelName: `profile-verification-${user?.id}`,
    table: 'user_profiles',
    filter: user?.id ? `id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onUpdate: (payload) => {
      const verified = payload?.new?.profile_verified;
      if (verified !== undefined) {
        setVerificationStatus(verified ? 'verified' : 'pending');
        if (verified) {
          setSaveStatus('saved');
        }
      }
    },
  });

  // Calculate completion for influencer
  const getInfluencerCompletion = () => {
    const fields = [];
    const hasHandle = Object.values(influencerData?.socialHandles)?.some(h => h?.trim());
    fields?.push(hasHandle);

    const filledPlatforms = Object.entries(influencerData?.socialHandles)
      ?.filter(([_, v]) => v?.trim())
      ?.map(([k]) => k);
    const hasFollowerRanges = filledPlatforms?.length > 0 &&
      filledPlatforms?.every(p => influencerData?.followerRanges?.[p]);
    fields?.push(hasFollowerRanges);

    const hasCategories = influencerData?.categories?.length > 0;
    fields?.push(hasCategories);

    const completed = fields?.filter(Boolean)?.length;
    return { completed, total: 3, percentage: Math.round((completed / 3) * 100) };
  };

  // Calculate completion for brand
  const getBrandCompletion = () => {
    const fields = [
      !!brandData?.brandName?.trim(),
      !!brandData?.websiteUrl?.trim(),
      !!brandData?.businessCategory,
      !!brandData?.campaignBudget,
    ];
    const completed = fields?.filter(Boolean)?.length;
    return { completed, total: 4, percentage: Math.round((completed / 4) * 100) };
  };

  // Calculate SocialCents score for influencer
  const calculateScore = () => {
    let score = 0;
    const handleCount = Object.values(influencerData?.socialHandles)?.filter(h => h?.trim())?.length;
    score += handleCount * 50;
    const followerScores = { '1k-10k': 50, '10k-50k': 100, '50k-100k': 150, '100k-500k': 200, '500k-1m': 250, '1m+': 300 };
    Object.values(influencerData?.followerRanges)?.forEach(r => { if (r) score += followerScores?.[r] || 0; });
    score += influencerData?.categories?.length * 30;
    return Math.min(score, 1000);
  };

  const completion = role === 'influencer' ? getInfluencerCompletion() : getBrandCompletion();
  const isComplete = completion?.percentage === 100;
  let score = role === 'influencer' ? calculateScore() : 0;

  const handleInfluencerChange = (field, value) => {
    setInfluencerData(prev => ({ ...prev, [field]: value }));
    setErrors({});
  };

  const handleBrandChange = (field, value) => {
    setBrandData(prev => ({ ...prev, [field]: value }));
    setErrors({});
  };

  const validateInfluencer = () => {
    const newErrors = {};
    const hasHandle = Object.values(influencerData?.socialHandles)?.some(h => h?.trim());
    if (!hasHandle) newErrors.socialHandles = 'Please provide at least one social media handle';

    const filledPlatforms = Object.entries(influencerData?.socialHandles)
      ?.filter(([_, v]) => v?.trim())
      ?.map(([k]) => k);
    filledPlatforms?.forEach(p => {
      if (!influencerData?.followerRanges?.[p]) {
        newErrors[`followerRanges.${p}`] = 'Please select follower range';
      }
    });

    if (influencerData?.categories?.length === 0) {
      newErrors.categories = 'Please select at least one content category';
    }
    return newErrors;
  };

  const validateBrand = () => {
    const newErrors = {};
    if (!brandData?.brandName?.trim()) newErrors.brandName = 'Brand name is required';
    if (!brandData?.websiteUrl?.trim()) newErrors.websiteUrl = 'Website URL is required';
    if (!brandData?.businessCategory) newErrors.businessCategory = 'Please select a business category';
    if (!brandData?.campaignBudget) newErrors.campaignBudget = 'Please select a campaign budget range';
    return newErrors;
  };

  const handleSubmit = async () => {
    setSubmitError('');
    const validationErrors = role === 'influencer' ? validateInfluencer() : validateBrand();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSaveStatus('saving');
    try {
      const profileUpdates = {
        profile_verified: true,
      };

      const { error } = await updateProfile(profileUpdates);
      if (error) {
        setSubmitError(error?.message || 'Failed to save profile. Please try again.');
        setSaveStatus('error');
        setIsSubmitting(false);
        return;
      }

      setSaveStatus('saved');

      // Redirect to role-specific dashboard
      if (role === 'brand') {
        navigate('/brand-dashboard');
      } else {
        navigate('/user-dashboard');
      }
    } catch (err) {
      setSubmitError('An unexpected error occurred. Please try again.');
      setSaveStatus('error');
      setIsSubmitting(false);
    }
  };

  const roleConfig = {
    influencer: {
      title: 'Influencer Profile Verification',
      subtitle: 'Complete your profile to unlock your SocialCents Score and start earning',
      icon: 'Star',
      color: 'var(--color-primary)',
      gradient: 'from-[var(--color-primary)] to-[var(--color-secondary)]',
      tooltipContent: 'Verifying your social accounts helps brands trust your reach and allows us to calculate your SocialCents Score accurately. Your data is secure and never shared without consent.',
      dashboardLabel: 'Influencer Dashboard',
    },
    brand: {
      title: 'Brand Partner Verification',
      subtitle: 'Complete your brand profile to start connecting with influencers and launching campaigns',
      icon: 'Building2',
      color: 'var(--color-secondary)',
      gradient: 'from-[var(--color-secondary)] to-[var(--color-accent)]',
      tooltipContent: 'Brand verification ensures authentic partnerships. Your company information helps influencers understand your brand and creates trust in the marketplace.',
      dashboardLabel: 'Brand Dashboard',
    },
    admin: {
      title: 'Admin Profile Verification',
      subtitle: 'Complete your admin profile to access the platform management tools',
      icon: 'ShieldCheck',
      color: 'var(--color-accent)',
      gradient: 'from-[var(--color-accent)] to-[var(--color-primary)]',
      tooltipContent: 'Admin verification ensures platform security and proper access control.',
      dashboardLabel: 'Admin Panel',
    },
  };

  const config = roleConfig?.[role] || roleConfig?.influencer;

  // Admin role: redirect directly
  useEffect(() => {
    if (role === 'admin') {
      navigate('/admin-panel');
    }
  }, [role]);

  return (
    <>
      <Helmet>
        <title>Complete Your Profile - SocialCents</title>
        <meta name="description" content="Complete your profile verification to access your SocialCents dashboard" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)] flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[var(--color-card)]/90 backdrop-blur-md border-b border-[var(--color-border)]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' }}
              >
                <Icon name="DollarSign" size={18} color="#FFFFFF" />
              </div>
              <span className="text-lg font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                SocialCents
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Real-time save status indicator */}
              {saveStatus && (
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                  saveStatus === 'saving' ? 'bg-primary/10 text-primary' :
                  saveStatus === 'saved'? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                }`}>
                  <Icon
                    name={saveStatus === 'saving' ? 'Loader' : saveStatus === 'saved' ? 'CheckCircle' : 'AlertCircle'}
                    size={12}
                    className={saveStatus === 'saving' ? 'animate-spin' : ''}
                  />
                  {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Error'}
                </div>
              )}
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ background: `linear-gradient(135deg, ${config?.color}20, ${config?.color}10)`, border: `1px solid ${config?.color}40` }}
              >
                <Icon name={config?.icon} size={12} color={config?.color} />
                <span style={{ color: config?.color }}>
                  {role?.charAt(0)?.toUpperCase() + role?.slice(1)}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 max-w-4xl mx-auto w-full px-4 md:px-6 py-8 md:py-12">
          {/* Hero Section */}
          <div className="text-center mb-10">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{
                background: `linear-gradient(135deg, ${config?.color}20, ${config?.color}10)`,
                border: `1px solid ${config?.color}40`,
                color: config?.color
              }}
            >
              <Icon name="Shield" size={14} />
              Profile Verification Required
            </div>
            <div className="flex items-center justify-center gap-3 mb-4">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[var(--color-foreground)]">
                {config?.title}
              </h1>
              <VerificationTooltip content={config?.tooltipContent} />
            </div>
            <p className="text-[var(--color-muted-foreground)] text-sm md:text-base max-w-xl mx-auto">
              {config?.subtitle}
            </p>
          </div>

          {/* Progress + Score Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="md:col-span-2">
              <VerificationProgressBar
                completedFields={completion?.completed}
                totalFields={completion?.total}
                percentage={completion?.percentage}
              />
            </div>
            {role === 'influencer' && (
              <div>
                <ScorePreviewBadge score={score} role={role} />
              </div>
            )}
            {role === 'brand' && (
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-2xl p-5 flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))' }}
                >
                  <Icon name="Rocket" size={22} color="#FFFFFF" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[var(--color-secondary)] uppercase tracking-wide mb-1">Campaign Ready</p>
                  <p className="text-sm font-bold text-[var(--color-foreground)]">Launch in minutes</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">After verification</p>
                </div>
              </div>
            )}
          </div>

          {/* Form Card */}
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-2xl p-6 md:p-8 shadow-xl">
            {/* Requirement Notice */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-[var(--color-muted)]/30 border border-[var(--color-border)] mb-8">
              <Icon name="AlertCircle" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
              <p className="text-xs text-[var(--color-muted-foreground)] leading-relaxed">
                <span className="font-semibold text-[var(--color-foreground)]">Verification required: </span>
                All fields marked with <span className="text-[var(--color-error)] font-semibold">*</span> must be completed before you can access your {config?.dashboardLabel}.
              </p>
            </div>

            {/* Role-Specific Form */}
            {role === 'influencer' ? (
              <InfluencerVerificationForm
                formData={influencerData}
                onChange={handleInfluencerChange}
                errors={errors}
              />
            ) : (
              <BrandVerificationForm
                formData={brandData}
                onChange={handleBrandChange}
                errors={errors}
              />
            )}

            {/* Submit Error */}
            {submitError && (
              <div className="mt-6 p-4 rounded-xl bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 flex items-center gap-3">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                <p className="text-sm text-[var(--color-error)]">{submitError}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isComplete || isSubmitting}
                className="w-full sm:w-auto flex-1 h-12 px-8 rounded-xl font-semibold text-base text-white transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: isComplete
                    ? `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`
                    : 'var(--color-muted)',
                  boxShadow: isComplete ? '0 4px 20px rgba(20, 184, 166, 0.3)' : 'none',
                }}
              >
                {isSubmitting ? (
                  <><Icon name="Loader" size={18} className="animate-spin" /> Saving...</>
                ) : (
                  <><Icon name="CheckCircle" size={18} /> Complete Verification</>
                )}
              </button>
              {!isComplete && (
                <p className="text-xs text-[var(--color-muted-foreground)] text-center sm:text-left">
                  Complete all required fields to enable this button
                </p>
              )}
            </div>

            {/* Fields Remaining */}
            {!isComplete && (
              <div className="mt-4 flex items-center gap-2">
                <div className="flex gap-1">
                  {Array.from({ length: completion?.total })?.map((_, i) => (
                    <div
                      key={i}
                      className="w-2 h-2 rounded-full transition-all duration-300"
                      style={{
                        background: i < completion?.completed
                          ? 'var(--color-primary)'
                          : 'var(--color-muted)'
                      }}
                    />
                  ))}
                </div>
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  {completion?.total - completion?.completed} section{completion?.total - completion?.completed !== 1 ? 's' : ''} remaining
                </span>
              </div>
            )}
          </div>

          {/* Benefits Footer */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {role === 'influencer' ? (
              <>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--color-card)]/50 border border-[var(--color-border)]">
                  <Icon name="Zap" size={18} color="var(--color-accent)" />
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-foreground)]">Earn Cashback</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">On every campaign post</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--color-card)]/50 border border-[var(--color-border)]">
                  <Icon name="TrendingUp" size={18} color="var(--color-success)" />
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-foreground)]">Grow Your Score</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">Unlock premium campaigns</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--color-card)]/50 border border-[var(--color-border)]">
                  <Icon name="Shield" size={18} color="var(--color-primary)" />
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-foreground)]">Verified Badge</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">Stand out to brands</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--color-card)]/50 border border-[var(--color-border)]">
                  <Icon name="Users" size={18} color="var(--color-secondary)" />
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-foreground)]">50K+ Creators</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">Ready to promote your brand</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--color-card)]/50 border border-[var(--color-border)]">
                  <Icon name="BarChart2" size={18} color="var(--color-accent)" />
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-foreground)]">Real-time Analytics</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">Track campaign performance</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--color-card)]/50 border border-[var(--color-border)]">
                  <Icon name="Shield" size={18} color="var(--color-success)" />
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-foreground)]">Verified Network</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">Authentic influencers only</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default ProfileVerificationScreen;
